/**
 * Browser preview mode (?preview=1).
 *
 * Telegram Mini Apps only receive initData when opened from the bot, so the
 * deployed app can't be viewed in a plain browser. Preview mode short-circuits
 * auth with a synthetic session and serves mock data by intercepting fetch, so
 * the real screens render for design review — without Telegram or a backend.
 *
 * Strictly opt-in via the query param: real bot sessions never set it.
 */
import type { AuthSession } from '../api/types'

export const isPreviewMode = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  }
  try {
    return new URLSearchParams(window.location.search).get('preview') === '1'
  } catch {
    return false
  }
}

export const previewSession = (): AuthSession => ({
  accessToken: 'preview',
  refreshToken: null,
  organizationId: 1,
  expiresIn: 3600,
  rfqId: null,
})

// ---------- mock data (snake_case — matches the API contract) ----------

const me = {
  displayName: 'Анна Демо',
  display_name: 'Анна Демо',
  email: 'preview@provisium.ru',
  organizations: [
    {
      id: 1,
      org_type: 'buyer',
      legal_name: 'ООО «Северный дворик»',
      trade_name: 'Северный дворик',
      plan: 'premium',
      plan_display_name: 'Premium',
    },
  ],
}

const S1 = 'ООО «Зелёный амбар»'
const S2 = 'ИП Орлова Марина Игоревна'
const S3 = 'ООО «КухняСклад»'

const suppliers = [
  { id: 1, legal_name: S1, display_label: null, inn: '7700000101' },
  { id: 2, legal_name: S2, display_label: null, inn: '5000000202' },
  { id: 3, legal_name: S3, display_label: null, inn: '7700000303' },
]

const supplierStats = [
  { private_supplier_id: 1, items_total: 46, last_updated_at: '2026-08-05T09:00:00Z', freshness_status: 'fresh' },
  { private_supplier_id: 2, items_total: 23, last_updated_at: '2026-08-02T09:00:00Z', freshness_status: 'stale_7' },
  { private_supplier_id: 3, items_total: 31, last_updated_at: '2026-07-19T09:00:00Z', freshness_status: 'stale_14' },
]

const summary = {
  total_items: 100,
  match_auto: 74,
  match_suggested: 12,
  match_manual: 8,
  match_unmatched: 6,
  needs_attention: 6,
  stale_items: 9,
  hidden_from_search: 0,
}

type MockItem = Record<string, unknown>
const item = (
  id: number,
  name: string,
  sid: number,
  sname: string,
  cat: string,
  price: number,
  unit: string,
  delta: number | null,
  country = 'Россия',
  brand: string | null = null,
): MockItem => ({
  id,
  organization_id: 1,
  name,
  position_name: name,
  supplier_name: sname,
  private_supplier_id: sid,
  private_supplier_name: sname,
  match_status: 'auto',
  brand,
  country,
  unit,
  price,
  product_type_id: id,
  product_type_name: cat,
  buyer_search_visible: true,
  freshness_status: 'fresh',
  updated_at: '2026-08-07T09:00:00Z',
  days_since_update: 2,
  previous_price: delta != null ? Math.round((price / (1 + delta / 100)) * 100) / 100 : null,
  price_delta_pct: delta,
  previous_observed_at: delta != null ? '2026-07-28T09:00:00Z' : null,
})

const items: MockItem[] = [
  item(101, 'Томаты розовые тепличные', 1, S1, 'Томаты', 189, 'кг', 8),
  item(102, 'Огурцы гладкие короткоплодные', 1, S1, 'Огурцы', 142, 'кг', -5),
  item(103, 'Перец болгарский красный', 1, S1, 'Перец', 268, 'кг', 9, 'Турция'),
  item(104, 'Салат Айсберг', 2, S2, 'Зелень', 96, 'шт', 9),
  item(105, 'Микрозелень микс', 2, S2, 'Зелень', 210, 'уп', null),
  item(201, 'Молоко 3,2% пастеризованное', 3, S3, 'Молоко', 78, 'л', 5, 'Россия', 'Утренний луг'),
  item(202, 'Сыр Моцарелла для пиццы', 3, S3, 'Сыр', 640, 'кг', -3, 'Россия', 'ПиццаФорм'),
  item(301, 'Филе бедра цыплёнка охл.', 1, S1, 'Птица', 318, 'кг', 6),
]

const priceMoves = [
  { catalog_item_id: 103, item_name: 'Перец болгарский красный', supplier_name: S1, private_supplier_id: 1, previous_price: 245, current_price: 268, delta_abs: 23, delta_pct: 9.4, observed_at: '2026-08-01T09:00:00Z', previous_observed_at: '2026-07-24T09:00:00Z' },
  { catalog_item_id: 104, item_name: 'Салат Айсберг', supplier_name: S2, private_supplier_id: 2, previous_price: 88, current_price: 96, delta_abs: 8, delta_pct: 9.1, observed_at: '2026-08-03T09:00:00Z', previous_observed_at: '2026-07-28T09:00:00Z' },
  { catalog_item_id: 201, item_name: 'Молоко 3,2%', supplier_name: S3, private_supplier_id: 3, previous_price: 74, current_price: 78, delta_abs: 4, delta_pct: 5.4, observed_at: '2026-08-05T09:00:00Z', previous_observed_at: '2026-07-29T09:00:00Z' },
  { catalog_item_id: 202, item_name: 'Сыр Моцарелла', supplier_name: S3, private_supplier_id: 3, previous_price: 662, current_price: 640, delta_abs: -22, delta_pct: -3.3, observed_at: '2026-08-06T09:00:00Z', previous_observed_at: '2026-07-30T09:00:00Z' },
  { catalog_item_id: 102, item_name: 'Огурцы гладкие', supplier_name: S1, private_supplier_id: 1, previous_price: 149, current_price: 142, delta_abs: -7, delta_pct: -4.7, observed_at: '2026-08-08T09:00:00Z', previous_observed_at: '2026-08-01T09:00:00Z' },
]

const priceHistory = [176, 175, 178, 182, 180, 185, 189].map((price, i) => ({
  observed_at: `2026-07-${String(10 + i * 4).padStart(2, '0')}T09:00:00Z`,
  price,
}))

const sampleBlank = `Бланк заказа · Провизиум

Покупатель: Северный дворик
Поставщик: ${S1} (ИНН 7700000101)
Доставка: 08.08.2026
————————————————
1. Томаты розовые тепличные
   10 кг × 189 руб. = 1890 руб.
2. Огурцы гладкие короткоплодные
   8 кг × 142 руб. = 1136 руб.
————————————————
Итого: 3026 руб. (2 позиции)`

// ---------- fetch interception ----------

const jsonResponse = (data: unknown): Response =>
  new Response(JSON.stringify(data), {
    status: 200,
    headers: { 'Content-Type': 'application/json' },
  })

const textResponse = (body: string): Response =>
  new Response(body, {
    status: 200,
    headers: { 'Content-Type': 'text/plain; charset=utf-8' },
  })

const matchMock = (rawUrl: string, method: string): Response | null => {
  let pathname: string
  try {
    pathname = new URL(rawUrl, window.location.origin).pathname
  } catch {
    pathname = rawUrl
  }
  const marker = '/api/v1'
  const idx = pathname.indexOf(marker)
  if (idx === -1) {
    return null
  }
  const p = pathname.slice(idx + marker.length) || '/'

  if (p === '/me') return jsonResponse(me)
  if (p.startsWith('/buyer/private-catalog/summary')) return jsonResponse(summary)
  if (p.startsWith('/buyer/private-catalog/items')) {
    let params: URLSearchParams
    try {
      params = new URL(rawUrl, window.location.origin).searchParams
    } catch {
      params = new URLSearchParams()
    }
    const q = (params.get('search') ?? '').trim().toLowerCase()
    const sid = params.get('privateSupplierId') ?? params.get('private_supplier_id')
    let filtered = items
    if (sid) {
      filtered = filtered.filter((it) => String(it.private_supplier_id) === sid)
    }
    if (q) {
      filtered = filtered.filter(
        (it) =>
          String(it.name).toLowerCase().includes(q) ||
          String(it.product_type_name).toLowerCase().includes(q),
      )
    }
    return jsonResponse({ items: filtered, page: 0, size: filtered.length, total_elements: filtered.length, total_pages: 1 })
  }
  if (p.startsWith('/buyer/private-catalog/filter-options')) return jsonResponse({ brands: [], countries: [] })
  if (p.startsWith('/buyer/private-catalog/suppliers/stats')) return jsonResponse(supplierStats)
  if (p.startsWith('/buyer/private-catalog/suppliers')) return jsonResponse(suppliers)
  if (p.startsWith('/buyer/market-analytics/price-moves')) return jsonResponse(priceMoves)
  if (p.startsWith('/buyer/market-analytics/price-history')) return jsonResponse(priceHistory)
  if (p.startsWith('/buyer/order-blanks')) return jsonResponse([])
  if (p.startsWith('/buyer/cart/order-blank') && method === 'POST') return textResponse(sampleBlank)

  // Unknown buyer endpoint under preview — succeed empty so screens don't error.
  return jsonResponse({})
}

let installed = false
export const installPreviewApi = (): void => {
  if (installed || typeof window === 'undefined') {
    return
  }
  installed = true
  const realFetch = window.fetch.bind(window)
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
    const method = (
      init?.method ?? (typeof input === 'object' && 'method' in input ? input.method : 'GET') ?? 'GET'
    ).toUpperCase()
    const mock = matchMock(url, method)
    if (mock) {
      return mock
    }
    return realFetch(input, init)
  }
}
